/**
 * pic.enable_free_gift_bundle + pic.gift_bundle
 * Adds gift variants as normal cart lines (not nested), once per product.
 * Removes those gifts only when the last linked variant leaves the cart.
 * Gift lines are reversed in the add payload so cart order matches the metafield list.
 */
(function (root, factory) {
  const api = factory(root);
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (root) {
    root.PiGiftBundle = api;
    if (root.document) api.install();
  }
})(typeof window !== 'undefined' ? window : globalThis, function (root) {
  const PARENT = '_pi_gift_bundle_product';
  const GIFT = '_pi_gift_bundle_for';
  const HEADER = 'X-Pi-Gift-Bundle';

  function propsOf(item) {
    return (item && item.properties) || {};
  }

  function parentId(item) {
    const value = propsOf(item)[PARENT];
    return value == null || value === '' ? '' : String(value);
  }

  function giftFor(item) {
    const value = propsOf(item)[GIFT];
    return value == null || value === '' ? '' : String(value);
  }

  /** True when this line is an eligible parent for gift product id `pid`. */
  function isLinkedParent(item, pid) {
    if (!item || !pid) return false;
    if (giftFor(item)) return false;
    const id = String(pid);
    if (parentId(item) === id) return true;
    return String(item.product_id || '') === id;
  }

  function bundleAlreadyInCart(cart, productId) {
    const pid = String(productId);
    return (cart.items || []).some((item) => parentId(item) === pid || giftFor(item) === pid);
  }

  /**
   * Cart shows the last added line first.
   * Prepend gifts in reverse metafield order and leave the caller's lines in place,
   * so this add displays as: product, then gifts in list order.
   * A later add is a new request and lands above this group without moving it.
   */
  function applyGiftBundle(config, cart, items) {
    const source = Array.isArray(items) ? items : [];
    const next = source.map(cloneItem);
    const variants = (config && config.variants) || {};
    const sellingPlans = (config && config.sellingPlans) || {};
    const giftBlocks = [];
    const scheduled = new Set();
    let stamped = false;

    next.forEach((item) => {
      if (!item || item.parent_id) return;
      const entry = variants[String(item.id)];
      if (!entry || !entry.productId) return;

      const pid = String(entry.productId);
      item.properties = { ...propsOf(item), [PARENT]: pid };
      stamped = true;

      if (scheduled.has(pid)) return;
      scheduled.add(pid);
      if (bundleAlreadyInCart(cart, pid)) return;
      if (next.some((line) => giftFor(line) === pid)) return;

      const seen = new Set();
      const gifts = [];
      (entry.gifts || []).forEach((id) => {
        const giftId = Number(id);
        if (!giftId || String(giftId) === String(item.id) || seen.has(giftId)) return;
        if (next.some((line) => Number(line.id) === giftId)) return;
        seen.add(giftId);
        gifts.push({
          id: giftId,
          quantity: 1,
          properties: { [GIFT]: pid },
          ...(sellingPlans[giftId] ? { selling_plan: sellingPlans[giftId] } : {}),
        });
      });

      if (gifts.length) giftBlocks.push(gifts.reverse());
    });

    if (!giftBlocks.length) {
      return { items: stamped ? next : source, changed: stamped, addedGifts: false };
    }

    return {
      items: [...giftBlocks.flat(), ...next],
      changed: true,
      addedGifts: true,
    };
  }

  function giftKeysToRemove(cart, removingKeys) {
    const removing = removingKeys instanceof Set ? removingKeys : new Set(removingKeys || []);
    const productIds = new Set();

    (cart.items || []).forEach((item) => {
      if (!removing.has(item.key)) return;
      const pid = parentId(item) || (giftFor(item) ? '' : String(item.product_id || ''));
      if (pid) productIds.add(pid);
    });

    const keys = [];
    productIds.forEach((pid) => {
      const stillLinked = (cart.items || []).some((item) => {
        if (removing.has(item.key)) return false;
        return isLinkedParent(item, pid);
      });
      if (stillLinked) return;
      (cart.items || []).forEach((item) => {
        if (giftFor(item) === pid && !removing.has(item.key)) keys.push(item.key);
      });
    });

    return keys;
  }

  function orphanGiftKeys(cart) {
    const items = cart.items || [];
    return items
      .filter((item) => {
        const pid = giftFor(item);
        if (!pid) return false;
        return !items.some((other) => isLinkedParent(other, pid));
      })
      .map((item) => item.key);
  }

  function cloneItem(item) {
    const next = {
      id: Number(item.id),
      quantity: Number(item.quantity) || 1,
    };
    if (item.selling_plan) next.selling_plan = item.selling_plan;
    if (item.parent_id) next.parent_id = Number(item.parent_id);
    const properties = propsOf(item);
    if (Object.keys(properties).length) next.properties = { ...properties };
    return next;
  }

  function cartDisplayOrder(requestItems) {
    return [...requestItems].reverse().map((item) => item.id);
  }

  function install() {
    if (root.__piGiftBundleReady) return;
    root.__piGiftBundleReady = true;

    const originalFetch = root.fetch.bind(root);
    let queue = Promise.resolve();
    let syncing = false;
    let cartCache = { items: [] };

    const routesRoot = () => root.Shopify?.routes?.root || '/';

    const enqueue = (task) => {
      const run = queue.then(task, task);
      queue = run.then(
        () => undefined,
        () => undefined
      );
      return run;
    };

    const readConfig = () => {
      const variants = {};
      const sellingPlans = {};
      document.querySelectorAll('[data-pi-gift-bundle-config]').forEach((el) => {
        try {
          const data = JSON.parse(el.textContent || '{}');
          Object.assign(variants, data.variants || {});
          Object.assign(sellingPlans, data.sellingPlans || {});
        } catch (error) {
          /* ignore malformed config */
        }
      });
      return { variants, sellingPlans };
    };

    const requestUrl = (input) => {
      if (typeof input === 'string') return input;
      if (input && typeof input.url === 'string') return input.url;
      return '';
    };

    const isMarked = (init) => {
      if (!init || !init.headers) return false;
      if (typeof Headers !== 'undefined' && init.headers instanceof Headers) {
        return init.headers.get(HEADER) === '1';
      }
      return init.headers[HEADER] === '1';
    };

    const parseBody = (body) => {
      if (!body) return null;
      if (typeof body === 'string') {
        try {
          return JSON.parse(body);
        } catch (error) {
          return null;
        }
      }
      if (typeof FormData !== 'undefined' && body instanceof FormData) {
        const data = {};
        body.forEach((value, key) => {
          data[key] = value;
        });
        return data;
      }
      if (typeof body === 'object') return body;
      return null;
    };

    const itemsFrom = (body) => {
      if (!body || typeof body !== 'object') return null;
      if (Array.isArray(body.items)) return body.items.map(cloneItem);
      if (body.id == null || body.id === '') return null;
      const properties = {};
      if (body.properties && typeof body.properties === 'object' && !Array.isArray(body.properties)) {
        Object.assign(properties, body.properties);
      }
      Object.keys(body).forEach((key) => {
        const match = /^properties\[(.*)\]$/.exec(key);
        if (match) properties[match[1]] = body[key];
      });
      const item = {
        id: Number(body.id),
        quantity: Number(body.quantity) || 1,
      };
      if (body.selling_plan) item.selling_plan = body.selling_plan;
      if (Object.keys(properties).length) item.properties = properties;
      return [item];
    };

    const sectionMeta = (body) => {
      const meta = {};
      if (!body || typeof body !== 'object') return meta;
      if (body.sections != null && body.sections !== '') meta.sections = body.sections;
      if (body.sections_url) meta.sections_url = body.sections_url;
      return meta;
    };

    const jsonInit = (init, body) => {
      const headers = new Headers(init && init.headers);
      headers.set('Content-Type', 'application/json');
      headers.set('Accept', 'application/json');
      headers.set(HEADER, '1');
      return {
        ...(init || {}),
        method: (init && init.method) || 'POST',
        headers,
        body: JSON.stringify(body),
      };
    };

    const rememberCart = (cart) => {
      // /cart/add.js often returns only the new lines. Never treat that as the full cart.
      if (!cart || !Array.isArray(cart.items)) return cartCache;
      if (typeof cart.item_count === 'number') {
        const qtySum = cart.items.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);
        if (cart.items.length > 0 && qtySum !== cart.item_count && qtySum < cart.item_count) {
          return cartCache;
        }
      }
      cartCache = cart;
      return cart;
    };

    const syncCartFromEvent = (event) => {
      const cart = event && event.detail && event.detail.cart;
      if (cart && Array.isArray(cart.items)) rememberCart(cart);
    };

    document.addEventListener('cart:change', syncCartFromEvent);
    document.documentElement.addEventListener('cart:change', syncCartFromEvent);

    const fetchCart = async () => {
      const response = await originalFetch(`${routesRoot()}cart.js`, {
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { Accept: 'application/json', [HEADER]: '1' },
      });
      if (!response.ok) return cartCache;
      return rememberCart(await response.json());
    };

    const notify = (cart) => {
      // Do not fire cart:change without section HTML (breaks Impact cart-drawer).
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      paintInstant(cart);
    };

    const formatMoney = (cents) => {
      const format = root.themeVariables?.settings?.moneyFormat || '${{amount}}';
      const value = (Number(cents) || 0) / 100;
      const amount = value.toFixed(2);
      const amountNoDecimals = String(Math.round(value));
      const withComma = amount.replace('.', ',');
      return String(format)
        .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, amountNoDecimals)
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, amountNoDecimals)
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, withComma)
        .replace(/\{\{\s*amount\s*\}\}/g, amount);
    };

    const paintInstant = (cart) => {
      if (!cart) return;
      const host = document.querySelector('.__instant.iyU2woGEZqwFyagfI');
      const count = Number(cart.item_count) || 0;
      document.querySelectorAll('[data-instant-dynamic-content-source="CART_COUNT"]').forEach((el) => {
        el.textContent = String(count);
      });
      document.querySelectorAll('cart-count').forEach((el) => {
        el.classList.toggle('opacity-0', count === 0);
        if ('itemCount' in el) el.itemCount = count;
        else el.textContent = String(count);
      });
      if (!host) return;
      host.querySelectorAll('[data-instant-dynamic-content-source="CART_TOTAL_PRICE"]').forEach((el) => {
        el.textContent = formatMoney(cart.total_price);
      });
      const empty = host.querySelector('.pi-cart-empty');
      const filled = host.querySelector('.pi-cart-filled');
      if (empty) empty.style.display = count > 0 ? 'none' : '';
      if (filled) filled.style.display = count > 0 ? '' : 'none';
    };

    const removeDomLines = (keys) => {
      keys.forEach((key) => {
        const escaped = typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(String(key)) : String(key);
        document.querySelectorAll(`[data-instant-form-line-item-key="${escaped}"]`).forEach((el) => el.remove());
        document.querySelectorAll(`[data-line-key="${escaped}"]`).forEach((el) => {
          const row = el.closest('line-item, tr');
          if (row) row.remove();
        });
      });
    };

    const postUpdate = async (updates, meta) => {
      const response = await originalFetch(`${routesRoot()}cart/update.js`, jsonInit(null, { updates, ...(meta || {}) }));
      if (!response.ok) return null;
      return response.json();
    };

    const cssEscape = (value) =>
      typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(String(value)) : String(value);

    const LEAVING = 'pi-cart-line--leaving';

    const markLeaving = (node) => {
      if (!node || node.nodeType !== 1) return;
      const form = node.matches('form[data-instant-form-line-item-key]')
        ? node
        : node.closest('form[data-instant-form-line-item-key]');
      const line = node.matches('.line-item') ? node : node.closest('.line-item');
      if (form) form.classList.add(LEAVING);
      if (line) line.classList.add(LEAVING);
    };

    const clearOrphans = async (cart) => {
      const keys = orphanGiftKeys(cart || { items: [] });
      if (!keys.length || syncing) return cart;
      syncing = true;
      try {
        const updates = {};
        keys.forEach((key) => {
          updates[key] = 0;
        });
        const host = document.querySelector('.__instant.iyU2woGEZqwFyagfI');
        const sectionId = host?.getAttribute('data-section-id');
        const meta = sectionId ? { sections: sectionId } : {};
        const next = await postUpdate(updates, meta);
        if (!next) return cart;
        rememberCart(next);
        removeDomLines(keys);
        paintInstant(next);
        return next;
      } finally {
        syncing = false;
      }
    };

    const fadeOrphanKeys = (keys) => {
      (keys || []).forEach((key) => {
        const escaped = cssEscape(key);
        document
          .querySelectorAll(
            `form[data-instant-form-line-item-key="${escaped}"], .line-item[data-pi-line-key="${escaped}"]`
          )
          .forEach(markLeaving);
      });
    };

    /** After Instant removes a stamped parent, drop orphan gifts with the same fade. */
    const scheduleOrphanCleanup = () => {
      enqueue(async () => {
        if (syncing) return;
        const cart = await fetchCart();
        const orphans = orphanGiftKeys(cart);
        if (!orphans.length) return;
        fadeOrphanKeys(orphans);
        const next = await clearOrphans(cart);
        if (next && next !== cart) notify(next);
      }).catch((error) => console.warn('[pi-gift-bundle] orphan cleanup failed', error));
    };

    const prepareAdd = async (body) => {
      const items = itemsFrom(body);
      if (!items) return null;
      const config = readConfig();
      if (!Object.keys(config.variants).length) return null;
      const cart = await fetchCart();
      const result = applyGiftBundle(config, cart, items);
      if (!result.changed) return null;

      if (!result.addedGifts && !Array.isArray(body.items) && items.length === 1) {
        const item = result.items[0];
        return {
          ...sectionMeta(body),
          id: item.id,
          quantity: item.quantity,
          properties: item.properties || {},
          ...(item.selling_plan ? { selling_plan: item.selling_plan } : {}),
        };
      }

      return { ...sectionMeta(body), items: result.items };
    };

    const readRequestBody = async (input, init) => {
      let bodySource = init && init.body;
      if ((bodySource == null || bodySource === '') && typeof Request !== 'undefined' && input instanceof Request) {
        try {
          bodySource = await input.clone().text();
        } catch (error) {
          bodySource = null;
        }
      }
      return parseBody(bodySource);
    };

    const keysLeavingCart = (cart, body) => {
      const keys = new Set();
      if (!body || typeof body !== 'object') return keys;
      const items = cart.items || [];

      const addToken = (token) => {
        const value = token == null ? '' : String(token);
        if (!value) return;
        const byKey = items.find((item) => item.key === value);
        if (byKey) {
          keys.add(byKey.key);
          return;
        }
        const byVariant = items.filter((item) => String(item.variant_id) === value);
        if (byVariant.length === 1) keys.add(byVariant[0].key);
      };

      if (Number(body.quantity) === 0) {
        addToken(body.id);
        if (body.line != null && body.line !== '') {
          const item = items[Number(body.line) - 1];
          if (item) keys.add(item.key);
        }
      }

      if (body.updates && typeof body.updates === 'object') {
        Object.keys(body.updates).forEach((key) => {
          if (Number(body.updates[key]) === 0) addToken(key);
        });
      }

      return keys;
    };

    const rememberResponse = (response) => {
      if (!response || !response.ok || typeof response.clone !== 'function') return response;
      response
        .clone()
        .json()
        .then((cart) => {
          if (cart && Array.isArray(cart.items)) rememberCart(cart);
        })
        .catch(() => undefined);
      return response;
    };

    const lineKeyOf = (node) =>
      (node && (node.getAttribute('data-instant-form-line-item-key') || node.getAttribute('data-pi-line-key'))) ||
      '';

    const linesByAttr = (attr, value) => {
      if (!value) return [];
      return Array.from(document.querySelectorAll(`[${attr}="${cssEscape(value)}"]`)).filter((node) =>
        node.matches('form[data-instant-form-line-item-key], .line-item')
      );
    };

    /**
     * Batch last-linked-variant + its gifts into one update.js so Instant
     * redraws them together. Uses live cart properties, not only DOM stamps.
     */
    const expandLastParentRemoval = async (input, init) => {
      const body = await readRequestBody(input, init);
      if (!body) return null;

      const mayRemove =
        Number(body.quantity) === 0 ||
        (body.updates && Object.keys(body.updates).some((key) => Number(body.updates[key]) === 0));
      if (!mayRemove) return null;

      let cart = cartCache && Array.isArray(cartCache.items) && cartCache.items.length ? cartCache : null;
      if (!cart) cart = await fetchCart();

      const removing = keysLeavingCart(cart, body);
      if (!removing.size) return null;

      const giftKeys = giftKeysToRemove(cart, removing);
      if (!giftKeys.length) return null;

      giftKeys.forEach((key) => fadeOrphanKeys([key]));

      const updates = {};
      if (body.updates && typeof body.updates === 'object') Object.assign(updates, body.updates);
      removing.forEach((key) => {
        updates[key] = 0;
      });
      giftKeys.forEach((key) => {
        updates[key] = 0;
      });

      return originalFetch(
        `${routesRoot()}cart/update.js`,
        jsonInit(init, { ...sectionMeta(body), updates })
      );
    };

    const handleAdd = async (input, init) => {
      const url = requestUrl(input);
      if (isMarked(init) || !/\/cart\/add/i.test(url)) return originalFetch(input, init);

      const parsed = await readRequestBody(input, init);
      if (!parsed) return originalFetch(input, init);

      try {
        const nextBody = await prepareAdd(parsed);
        const response = nextBody
          ? await originalFetch(url, jsonInit(init, nextBody))
          : await originalFetch(input, init);
        // Refresh full cart after add so last-parent checks use real line keys.
        fetchCart().catch(() => undefined);
        return rememberResponse(response);
      } catch (error) {
        console.warn('[pi-gift-bundle] cart sync failed', error);
        return originalFetch(input, init);
      }
    };

    const handleChange = async (input, init) => {
      try {
        const expanded = await expandLastParentRemoval(input, init);
        if (expanded) {
          const response = rememberResponse(expanded);
          // Gifts already included; still reconcile in case of race.
          Promise.resolve(response)
            .then((res) => (res && res.ok ? scheduleOrphanCleanup() : undefined))
            .catch(() => undefined);
          return response;
        }
      } catch (error) {
        console.warn('[pi-gift-bundle] expand removal failed', error);
      }

      const response = rememberResponse(await originalFetch(input, init));
      Promise.resolve(response)
        .then((res) => (res && res.ok ? scheduleOrphanCleanup() : undefined))
        .catch(() => undefined);
      return response;
    };

    root.fetch = function (input, init) {
      const url = requestUrl(input);
      if (isMarked(init)) return originalFetch(input, init);

      if (/\/cart\/add(\.js)?(\?|$)/i.test(url)) {
        return enqueue(() => handleAdd(input, init));
      }

      if (/\/cart\/(change|update)(\.js)?(\?|$)/i.test(url)) {
        // Do not queue behind add: Instant Remove must start immediately.
        return handleChange(input, init);
      }

      return originalFetch(input, init);
    };

    document.addEventListener('cart:change', (event) => {
      if (event?.detail?.baseEvent === 'pi-gift-bundle') return;
      scheduleOrphanCleanup();
    });
    document.addEventListener('instant:cart-updated', scheduleOrphanCleanup);

    const nativeOpen = XMLHttpRequest.prototype.open;
    const nativeSend = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.open = function (method, url) {
      this.__piGiftUrl = url;
      return nativeOpen.apply(this, arguments);
    };
    XMLHttpRequest.prototype.send = function (body) {
      const url = String(this.__piGiftUrl || '');
      if (this.__piGiftPass || !/\/cart\/add(\.js)?(\?|$)/i.test(url)) {
        return nativeSend.call(this, body);
      }
      try {
        const parsed = parseBody(typeof body === 'string' ? body : null);
        if (parsed) {
          const items = itemsFrom(parsed);
          const result = items ? applyGiftBundle(readConfig(), cartCache, items) : null;
          if (result && result.changed) {
            const nextBody =
              !result.addedGifts && !Array.isArray(parsed.items) && result.items.length === 1
                ? {
                    ...sectionMeta(parsed),
                    id: result.items[0].id,
                    quantity: result.items[0].quantity,
                    properties: result.items[0].properties || {},
                  }
                : { ...sectionMeta(parsed), items: result.items };
            this.__piGiftPass = true;
            try {
              this.setRequestHeader('Content-Type', 'application/json');
              this.setRequestHeader(HEADER, '1');
            } catch (error) {
              /* header already set */
            }
            return nativeSend.call(this, JSON.stringify(nextBody));
          }
        }
      } catch (error) {
        console.warn('[pi-gift-bundle] request rewrite failed', error);
      }
      return nativeSend.call(this, body);
    };

    const fadeRemoval = (lineKey, productId) => {
      const key = String(lineKey || '');
      let pid = String(productId || '');

      if (!pid && key) {
        const line = (cartCache.items || []).find((item) => item.key === key);
        pid = line ? parentId(line) : '';
      }

      if (!key && !pid) return;

      if (pid) {
        const sources = [
          ...linesByAttr('data-pi-gift-bundle-product', pid),
          ...linesByAttr('data-product-id', pid),
        ].filter((node, index, all) => all.indexOf(node) === index);
        const anotherInDom = sources.some((node) => {
          const nodeKey = lineKeyOf(node);
          if (!nodeKey || nodeKey === key) return false;
          if (node.getAttribute('data-pi-gift-bundle-for') || node.classList.contains('line-item--gift')) {
            return false;
          }
          return true;
        });
        const anotherInCart = (cartCache.items || []).some(
          (item) => item.key !== key && isLinkedParent(item, pid)
        );
        const anotherSource = anotherInDom || anotherInCart;

        if (key) {
          const escaped = cssEscape(key);
          document
            .querySelectorAll(
              `form[data-instant-form-line-item-key="${escaped}"], .line-item[data-pi-line-key="${escaped}"]`
            )
            .forEach(markLeaving);
        } else {
          sources.forEach((node) => {
            if (lineKeyOf(node) === key) markLeaving(node);
          });
        }

        if (!anotherSource) {
          linesByAttr('data-pi-gift-bundle-for', pid).forEach(markLeaving);
          // DOM stamps may be missing after Instant redraw; fade by gift keys from cache.
          (cartCache.items || []).forEach((item) => {
            if (giftFor(item) === pid) fadeOrphanKeys([item.key]);
          });
        }
      } else if (key) {
        const escaped = cssEscape(key);
        document
          .querySelectorAll(
            `form[data-instant-form-line-item-key="${escaped}"], .line-item[data-pi-line-key="${escaped}"]`
          )
          .forEach(markLeaving);
      }
    };

    // Visual fade only. Instant still owns the Remove click.
    document.addEventListener(
      'click',
      (event) => {
        const button = event.target?.closest?.(
          '.__instant.iyU2woGEZqwFyagfI [data-instant-action-type="adjust-line-item-quantity"]'
        );
        if (button) {
          const form = button.closest('form[data-instant-form-line-item-key]');
          fadeRemoval(
            form?.getAttribute('data-instant-form-line-item-key') ||
              button.getAttribute('data-instant-action-line-item-key'),
            form?.getAttribute('data-pi-gift-bundle-product') || ''
          );
          return;
        }

        const decrement = event.target?.closest?.(
          '.__instant.iyU2woGEZqwFyagfI [data-instant-action-type="quantity-decrement"]'
        );
        if (decrement) {
          const form = decrement.closest('form[data-instant-form-line-item-key]');
          const qty = Number(form?.querySelector('input[name="quantity"]')?.value);
          if (!form || qty > 1) return;
          fadeRemoval(
            form.getAttribute('data-instant-form-line-item-key'),
            form.getAttribute('data-pi-gift-bundle-product') || ''
          );
          return;
        }

        const link = event.target?.closest?.('a[href*="/cart/change"]');
        if (!link || !link.href.includes('quantity=0')) return;
        const row = link.closest('tr');
        const line = link.closest('.line-item') || row?.querySelector('.line-item');
        let key = line?.getAttribute('data-pi-line-key') || '';
        if (!key) {
          try {
            key = new URL(link.href, root.location.href).searchParams.get('id') || '';
          } catch (error) {
            key = '';
          }
        }
        fadeRemoval(key, line?.getAttribute('data-pi-gift-bundle-product') || '');
      },
      true
    );

    const prime = () => {
      enqueue(async () => {
        const cart = await fetchCart();
        const next = await clearOrphans(cart);
        if (next && next !== cart) notify(next);
      }).catch(() => undefined);
    };
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', prime);
    else prime();
  }

  return {
    applyGiftBundle,
    giftKeysToRemove,
    orphanGiftKeys,
    isLinkedParent,
    bundleAlreadyInCart,
    cartDisplayOrder,
    install,
    PARENT,
    GIFT,
  };
});
